/**
 * CSAGE-364: the viewer's load/error state machine, extracted as a pure
 * function so it can be tested without mounting the whole embed shell
 * (QueryClientProvider + AuthBootstrap + portal postMessage handshake).
 *
 * ReportViewer used to decide with:
 *
 *   if (reportQ.isLoading || myParamsQ.isLoading) return <Loading/>
 *   if (reportQ.error || !reportQ.data)           return <"Report unavailable."/>
 *
 * Two defects in three lines, and together they produce a hard, stuck error
 * banner for a report that is perfectly fine — which is what the Mood Media
 * customer saw, and why a page reload "fixed" it.
 *
 * 1. `!reportQ.data` is NOT an error. In react-query v5
 *    `isLoading === isPending && isFetching`, so an ENABLED query that is
 *    pending but not currently fetching reports `isLoading === false` with
 *    `data === undefined` and `isError === false`. That falls through the
 *    first guard and lands on the second — rendering a failure for a request
 *    that has not failed. Reachable states: the tick after a query is created
 *    but before its fetch starts; a query re-keyed by the tenant/scope hash
 *    function (see `tenantScopedHashFn`), which lands the observer on a fresh
 *    empty cache entry; and `fetchStatus: 'paused'` when the browser is
 *    offline, which persists until connectivity returns.
 *
 * 2. It threw the server's error away. Every failure — 404 `report.not_found`,
 *    a 500, an expired token — rendered the same four words, so the only way
 *    to learn what actually happened was to ask the customer to reproduce it
 *    with devtools open. That cost a full diagnostic round-trip on CSAGE-364.
 *
 * The rule now: an error banner requires `isError`. Absence of data is
 * loading, forever if need be — a spinner that is wrong is recoverable, an
 * error that is wrong sends the customer to support.
 */

/** The subset of a react-query result this decision needs. */
export interface QueryStateLike {
  readonly isError: boolean;
  readonly error: unknown;
  readonly hasData: boolean;
  readonly isPending: boolean;
}

export type ReportViewState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'error'; readonly code: string | null; readonly message: string }
  | { readonly kind: 'ready' };

/**
 * Pull the platform error shape out of whatever `apiFetch` threw.
 *
 * `apiFetch` rejects with an `Error` carrying `status` and `body`, where body is
 * the `{ category, code, message }` envelope from @canopy/shared (or a
 * synthesized `{ code: 'http.<status>' }` when the response wasn't JSON). Both
 * shapes are handled, as is a non-Error rejection, so this can never itself
 * throw while rendering an error path.
 */
export function describeApiError(error: unknown): { code: string | null; message: string } {
  const body = (error as { body?: unknown })?.body;
  const code =
    typeof (body as { code?: unknown })?.code === 'string' ? (body as { code: string }).code : null;
  const bodyMessage = (body as { message?: unknown })?.message;
  const message =
    (typeof bodyMessage === 'string' && bodyMessage.trim() ? bodyMessage : null) ??
    (error instanceof Error && error.message.trim() ? error.message : null) ??
    'Unknown error';
  return { code, message };
}

/**
 * CSAGE-387: the copy shown when a write fails because the credential is dead.
 *
 * Sentence case, says what happened AND what to do, no raw code — the three
 * things `design-standards/standards/content-style.md` §7 asks for. The
 * previous behaviour on this path was `<pre>{JSON.stringify(err.body)}</pre>`,
 * which showed a Mood Media user `{"category":"auth","code":
 * "auth.invalid_token"}` and left them to infer the rest.
 */
export const SESSION_EXPIRED_MESSAGE = 'Your session expired. Reload the page, then try again.';

export interface WriteFailure {
  readonly code: string | null;
  /** Ready to render — already substituted for an expired session. */
  readonly message: string;
  /** True when a reload is genuinely the fix. */
  readonly sessionExpired: boolean;
}

/**
 * Describe a failed WRITE for display (CSAGE-387).
 *
 * Keyed on HTTP 401 rather than on the error code, deliberately:
 * `mapAuthFailureToResponse` collapses expired / disabled / locked / introspection
 * -unreachable into one generic `auth.invalid_token` on purpose (no oracle for a
 * caller probing credentials), so the code cannot tell us more than the status
 * already does.
 *
 * Two 401s are excluded because "reload" is the wrong instruction for them:
 *   * `auth.scope.unprovisioned` — the session is fine, the user has no
 *     entitlement for this hierarchy node. Reloading produces the same 401.
 *   * `auth.unsupported_mechanism` — the client sent a scheme the server
 *     doesn't support. A user cannot fix that at all.
 *
 * Note what a 401 arriving here now means: `apiFetch` has ALREADY refreshed the
 * credential and replayed the request once (see `client.ts` sendWithRefresh).
 * So this is the state after the automatic recovery failed, which is why the
 * copy asks for a reload rather than silently hinting the user retry.
 */
/**
 * CSAGE-384: copy for a subscription the server refused because the session
 * could not tell it where in the org tree the user is standing.
 *
 * The server's message is customer-safe but describes the CAUSE ("no hierarchy
 * scope"); this says what to do, in the terms the user is actually in. Saying
 * plainly that the subscription would otherwise deliver the wrong data matters:
 * the failure the guard replaces was silent, and a user who is merely told
 * "something went wrong" will try again rather than reload.
 */
export const SCOPE_UNRESOLVED_MESSAGE =
  'Canopy could not tell which part of your organization you are viewing, so this ' +
  'subscription would deliver the wrong data. Reload the page and try again. If it ' +
  'keeps happening, contact support.';

export function describeWriteFailure(error: unknown): WriteFailure {
  const { code, message } = describeApiError(error);
  const status = (error as { status?: unknown })?.status;
  // CSAGE-384: a 400 carrying this code is not a malformed body the user can fix
  // by editing the form — the session lacks a resolvable hierarchy scope, so the
  // remedy is a reload, exactly as for a dead credential.
  if (code === 'schedule.scope_unresolved') {
    return { code, message: SCOPE_UNRESOLVED_MESSAGE, sessionExpired: false };
  }
  const sessionExpired =
    status === 401 && code !== 'auth.scope.unprovisioned' && code !== 'auth.unsupported_mechanism';
  return {
    code,
    message: sessionExpired ? SESSION_EXPIRED_MESSAGE : message,
    sessionExpired,
  };
}

/**
 * Resolve what the viewer should render.
 *
 * `myParams` (the per-user saved parameter set) is deliberately non-blocking on
 * failure: it is an enhancement layered over the report's own defaults, so a
 * failing `parameters/mine` must degrade to those defaults rather than hide a
 * report the user can see. It is only waited on while genuinely pending.
 */
export function resolveReportViewState(
  report: QueryStateLike,
  myParams: QueryStateLike,
): ReportViewState {
  // The report itself is the only hard dependency.
  if (report.isError) return { kind: 'error', ...describeApiError(report.error) };
  if (!report.hasData) return { kind: 'loading' };
  // Hold the first paint until the user's saved params land, so RenderPanel
  // mounts once with the merged seed instead of flashing the shared defaults
  // and re-seeding. An ERRORED params fetch is not worth waiting for.
  if (myParams.isPending && !myParams.isError) return { kind: 'loading' };
  return { kind: 'ready' };
}
